import React, { useState } from 'react';
import { useNavigate, Navigate } from 'react-router-dom';
import { LogIn, Loader2 } from 'lucide-react';
import { useAdminAuth } from '../../context/AdminAuthContext';

const AdminLoginPage = () => {
  const { user, login, loading } = useAdminAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  if (loading) return <div className="min-h-screen flex items-center justify-center text-stone-400">Loading...</div>;
  if (user) return <Navigate to="/admin" replace />;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSubmitting(true);
    try {
      await login(email, password);
      navigate('/admin', { replace: true });
    } catch (err) {
      setError(err.message || 'Login failed');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="min-h-screen flex items-center justify-center bg-gradient-to-br from-stone-900 via-stone-800 to-rose-900 px-6"
      data-testid="admin-login-page"
    >
      <div className="w-full max-w-md bg-white rounded-2xl shadow-2xl p-10">
        <div className="flex items-center justify-center w-12 h-12 rounded-full bg-rose-50 mb-6">
          <LogIn className="w-5 h-5 text-rose-500" />
        </div>
        <h1
          className="font-serif text-3xl text-stone-900 mb-2"
          style={{ fontFamily: "'Playfair Display', serif" }}
        >
          Admin Console
        </h1>
        <p className="text-stone-500 text-sm mb-8">
          Sign in to manage messages and the blog.
        </p>

        <form onSubmit={handleSubmit} className="space-y-5">
          <div>
            <label htmlFor="email" className="block text-xs tracking-[0.2em] uppercase text-stone-600 mb-2">
              Email
            </label>
            <input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              data-testid="admin-login-email"
              className="w-full px-4 py-3 border border-stone-200 rounded-lg focus:border-rose-400 focus:ring-1 focus:ring-rose-400 outline-none"
            />
          </div>
          <div>
            <label htmlFor="password" className="block text-xs tracking-[0.2em] uppercase text-stone-600 mb-2">
              Password
            </label>
            <input
              id="password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              data-testid="admin-login-password"
              className="w-full px-4 py-3 border border-stone-200 rounded-lg focus:border-rose-400 focus:ring-1 focus:ring-rose-400 outline-none"
            />
          </div>

          {error && (
            <div data-testid="admin-login-error" className="text-rose-600 text-sm">{error}</div>
          )}

          <button
            type="submit"
            disabled={submitting}
            data-testid="admin-login-submit"
            className="w-full bg-rose-400 hover:bg-rose-500 disabled:opacity-50 text-white py-3 rounded-full font-semibold transition-colors flex items-center justify-center gap-2"
          >
            {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
            Sign in
          </button>
        </form>
      </div>
    </div>
  );
};

export default AdminLoginPage;
